import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faHandshake } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  RowCustomAction,
  SortDirection,
  UserPermission
} from '@globaleris/ui.library'
import { Box, CardProps, Chip } from '@material-ui/core'
import { Archive, Block, Delete, Update, Visibility } from '@material-ui/icons'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { QuotesModel } from '../../../../../../../api/Argentia/endpoints/quotes/model/quotes'
import { EnumEstadoLead } from '../../../../../../../api/oData/enums/enumEstadoLead'
import { EnumEstadoOferta } from '../../../../../../../api/oData/enums/enumEstadoOferta'
import { EnumTipoLead } from '../../../../../../../api/oData/enums/enumTipoLead'
import { CardActionProps } from '../../../../../../../components/shared/cardActions/cardActions'
import { ButtonOption } from '../../../../../../../components/shared/splitButton/splitButton'
import { StandardCard } from '../../../../../../../components/shared/standardCard/standardCard'
import { enumToSelectOptionArray } from '../../../../../../../helpers'
import { getCustomerDomain } from '../../../../../../../redux'
import { EvaluateLeadButtonOptions } from '../../../../../leads.model'

interface QuotesCardProps extends CardProps {
  isLoading: boolean
  draftQuotes: QuotesModel[]
  publishedQuotes: QuotesModel[]
  historicQuotes: QuotesModel[]
  inactiveQuotes: QuotesModel[]
  customTitle?: string
  leadType?: EnumTipoLead
  leadState?: EnumEstadoLead
  onViewDetailClick: (selectedRow: QuotesModel) => void
  onReEvaluateQuoteClick?: (selectedRow: QuotesModel) => void
  onEvaluateLeadClick?: (option: ButtonOption) => void
  onDeactivateClick?: (selectedRow: QuotesModel) => void
  onMoveToHistoricClick?: (selectedRow: QuotesModel) => void
  onDeleteClick?: (selectedRow: QuotesModel) => void
  onPublishLeadClick?: () => void
  onViewReportClick?: () => void
}

export const QuotesCard: React.FC<QuotesCardProps> = ({
  isLoading,
  draftQuotes,
  publishedQuotes,
  historicQuotes,
  inactiveQuotes,
  customTitle,
  onViewDetailClick,
  onReEvaluateQuoteClick,
  // onViewContractsClick,  //disabled from US #65289 - Task #68868
  onEvaluateLeadClick,
  onDeactivateClick,
  onMoveToHistoricClick,
  onDeleteClick,
  onPublishLeadClick,
  onViewReportClick,
  ...props
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'leads', 'enums'])
  const history = useHistory()

  const [selectedTab, setSelectedTab] = useState<EnumEstadoOferta | null>(null)

  useEffect(() => {
    setSelectedTab(prev => {
      if (prev === EnumEstadoOferta.Borrador && draftQuotes.length > 0) return prev
      if (prev === EnumEstadoOferta.Publicada && publishedQuotes.length > 0) return prev
      if (prev === EnumEstadoOferta.Historica && historicQuotes.length > 0) return prev
      if (prev === EnumEstadoOferta.Baja && inactiveQuotes.length > 0) return prev
      if (draftQuotes.length > 0) return EnumEstadoOferta.Borrador
      if (publishedQuotes.length > 0) return EnumEstadoOferta.Publicada
      if (historicQuotes.length > 0) return EnumEstadoOferta.Historica
      if (inactiveQuotes.length > 0) return EnumEstadoOferta.Baja
      return null
    })
  }, [draftQuotes, publishedQuotes, historicQuotes, inactiveQuotes])

  const displayData = useMemo<QuotesModel[]>(() => {
    if (selectedTab === EnumEstadoOferta.Borrador) return draftQuotes
    if (selectedTab === EnumEstadoOferta.Publicada) return publishedQuotes
    if (selectedTab === EnumEstadoOferta.Historica) return historicQuotes
    if (selectedTab === EnumEstadoOferta.Baja) return inactiveQuotes
    return []
  }, [selectedTab, draftQuotes, publishedQuotes, historicQuotes, inactiveQuotes])

  const noData =
    draftQuotes.length === 0 &&
    publishedQuotes.length === 0 &&
    historicQuotes.length === 0 &&
    inactiveQuotes.length === 0
  const isHistoricTab =
    selectedTab === EnumEstadoOferta.Historica || selectedTab === EnumEstadoOferta.Baja
  const leadTypeIsPersonalizado = props.leadType === EnumTipoLead.Personalizado
  const leadState = props.leadState
  const isEvaluateDisabled =
    leadState === EnumEstadoLead.EvaluacionSolicitada || leadState === EnumEstadoLead.Procesando
  const isLeadEvaluado = leadState === EnumEstadoLead.Evaluado
  let disableOptions: number[] = []
  const splitButtonOptionsDefinition: ButtonOption[] = [
    {
      name: 'evaluateLead',
      label: t('leads:components.leadFile.quotesCard.footerActions.evaluateLead'),
      id: EvaluateLeadButtonOptions.EVALUATE_LEAD
    },
    {
      name: 'evaluateWithinDate',
      label: t('leads:components.leadFile.quotesCard.footerActions.evaluateWithinDate'),
      id: EvaluateLeadButtonOptions.EVALUATE_WITHIN_DATE
    }
  ]

  if (!leadTypeIsPersonalizado) {
    disableOptions.push(
      splitButtonOptionsDefinition.findIndex(
        option => option.id === EvaluateLeadButtonOptions.EVALUATE_WITHIN_DATE
      )
    )
  }

  // #region Handlers Starts
  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  const getRowCustomActions = (): RowCustomAction[] => {
    const rowCustomActions: RowCustomAction[] = [
      {
        icon: <Visibility fontSize="small" />,
        tooltip: t('leads:components.leadFile.quotesCard.dataTable.tooltips.viewDetail'),
        onClick: onViewDetailClick
      }
    ]

    if (!isHistoricTab && onReEvaluateQuoteClick) {
      rowCustomActions.push({
        icon: <Update fontSize="small" />,
        tooltip: t('leads:components.leadFile.quotesCard.dataTable.tooltips.reEvaluate'),
        onClick: onReEvaluateQuoteClick,
        userPermission: UserPermission.EDIT
      })
    }
    //disabled from US #65289 - Task #68868
    // if (onViewContractsClick) {
    //   rowCustomActions.push({
    //     icon: <FontAwesomeIcon size={'sm'} icon={faFileContract as IconProp} />,
    //     tooltip: t('leads:components.leadFile.quotesCard.dataTable.tooltips.viewContracts'),
    //     onClick: onViewContractsClick
    //   })
    // }
    if (!isHistoricTab && selectedTab !== EnumEstadoOferta.Borrador && onDeactivateClick) {
      rowCustomActions.push({
        icon: <Block fontSize="small" />,
        tooltip: t('leads:components.leadFile.quotesCard.dataTable.tooltips.deactivate'),
        onClick: onDeactivateClick
      })
    }

    if (selectedTab === EnumEstadoOferta.Publicada && onMoveToHistoricClick) {
      rowCustomActions.push({
        icon: <Archive fontSize="small" />,
        tooltip: t('leads:components.leadFile.quotesCard.dataTable.tooltips.moveToHistoric'),
        onClick: onMoveToHistoricClick
      })
    }

    if (selectedTab === EnumEstadoOferta.Borrador && onDeleteClick) {
      rowCustomActions.push({
        icon: <Delete fontSize="small" />,
        tooltip: t('leads:components.leadFile.quotesCard.dataTable.tooltips.delete'),
        onClick: onDeleteClick
      })
    }

    return rowCustomActions
  }

  const options: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    elevation: 0,
    download: false,
    canInsert: false,
    customInsertEditModal: true,
    sortOrder: { name: 'id', direction: SortDirection.DESC },
    rowCustomActions: getRowCustomActions(),
    analytics: {
      customer: getCustomerDomain(),
      view: t('leads:components.leadFile.quotesCard.dataTable.analytics')
    }
  }

  const columns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'priceList',
      display: false
    },
    {
      name: 'code',
      label: t('leads:components.leadFile.quotesCard.dataTable.columns.code'),
      type: DataType.TEXT
    },
    {
      name: 'description',
      label: t('leads:components.leadFile.quotesCard.dataTable.columns.description'),
      type: DataType.TEXT
    },
    {
      name: 'startDate',
      label: t('leads:components.leadFile.quotesCard.dataTable.columns.startDate'),
      type: DataType.DATE
    },
    {
      name: 'endDate',
      label: t('leads:components.leadFile.quotesCard.dataTable.columns.endDate'),
      type: DataType.DATE
    }
  ]
  // #endregion DataTable Config Ends

  // #region footerActions Config Starts
  const footerActions: CardActionProps[] = []

  if (onEvaluateLeadClick) {
    footerActions.push({
      splitButton: {
        options: splitButtonOptionsDefinition,
        onClick: onEvaluateLeadClick,
        hasPermission: true,
        disabledOptions: disableOptions,
        disabled: isEvaluateDisabled
      }
    })
  }

  if (selectedTab === EnumEstadoOferta.Borrador && onPublishLeadClick) {
    footerActions.push({
      label: t('leads:components.leadFile.quotesCard.footerActions.publishLead'),
      handleActionClick: onPublishLeadClick,
      hasPermission: true,
      disabled: !isLeadEvaluado
    })
  }

  if (onViewReportClick) {
    footerActions.push({
      label: t('leads:components.leadFile.quotesCard.footerActions.viewReport'),
      handleActionClick: onViewReportClick,
      hasPermission: true
    })
  }
  // #endregion footerActions Config Ends

  const chipSelector = (
    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
      {draftQuotes.length > 0 && (
        <Chip
          label={t('leads:components.leadFile.quotesCard.chips.borrador')}
          size="small"
          onClick={() => setSelectedTab(EnumEstadoOferta.Borrador)}
          color={selectedTab === EnumEstadoOferta.Borrador ? 'primary' : 'default'}
          clickable
        />
      )}
      {publishedQuotes.length > 0 && (
        <Chip
          label={t('leads:components.leadFile.quotesCard.chips.publicada')}
          size="small"
          onClick={() => setSelectedTab(EnumEstadoOferta.Publicada)}
          color={selectedTab === EnumEstadoOferta.Publicada ? 'primary' : 'default'}
          clickable
        />
      )}
      {historicQuotes.length > 0 && (
        <Chip
          label={t('leads:components.leadFile.quotesCard.chips.historicas')}
          size="small"
          onClick={() => setSelectedTab(EnumEstadoOferta.Historica)}
          color={selectedTab === EnumEstadoOferta.Historica ? 'primary' : 'default'}
          clickable
        />
      )}
      {inactiveQuotes.length > 0 && (
        <Chip
          label={t('leads:components.leadFile.quotesCard.chips.inactive')}
          size="small"
          onClick={() => setSelectedTab(EnumEstadoOferta.Baja)}
          color={selectedTab === EnumEstadoOferta.Baja ? 'primary' : 'default'}
          clickable
        />
      )}
    </Box>
  )

  return (
    <StandardCard
      avatarIcon={<FontAwesomeIcon size={'lg'} icon={faHandshake as IconProp} />}
      title={customTitle || t('leads:components.leadFile.quotesCard.title')}
      emptyCaption={t('leads:components.leadFile.quotesCard.emptyCaption')}
      showEmptyContent={noData}
      headerActions={!noData ? chipSelector : undefined}
      splitButtonOptions={
        splitButtonOptionsDefinition.length > 0 ? splitButtonOptionsDefinition : undefined
      }
      onSplitButtonClick={onEvaluateLeadClick}
      disabledOptions={disableOptions}
      splitButtonDisabled={isEvaluateDisabled}
      emptyButtonCaption={t('leads:components.leadFile.quotesCard.mainAction')}
      isLoading={isLoading}
      footerActions={noData ? undefined : footerActions.length > 0 ? footerActions : undefined}
      {...props}
    >
      <DataTable columns={columns} options={options} data={displayData} />
    </StandardCard>
  )
}
